import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

function parseVideoUrl(url = '') {
  const safeUrl = String(url).trim();

  let match = safeUrl.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (match) return { provider: 'youtube', id: match[1] };

  match = safeUrl.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
  if (safeUrl.includes('youtube.com/watch') && match) {
    return { provider: 'youtube', id: match[1] };
  }

  match = safeUrl.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/);
  if (match) return { provider: 'youtube', id: match[1] };

  match = safeUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (match) return { provider: 'vimeo', id: match[1] };

  return { provider: null, id: null };
}

function getEmbedUrl(provider, id) {
  if (!provider || !id) return '';

  if (provider === 'youtube') {
    return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&playsinline=1&rel=0&modestbranding=1&enablejsapi=1`;
  }

  if (provider === 'vimeo') {
    return `https://player.vimeo.com/video/${id}?autoplay=1&title=0&byline=0&portrait=0&api=1`;
  }

  return '';
}

function VideoPlayer({ videoUrl, thumbnailUrl }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const { provider, id } = useMemo(() => parseVideoUrl(videoUrl), [videoUrl]);
  const embedUrl = useMemo(() => getEmbedUrl(provider, id), [provider, id]);
  const handlePlay = () => setIsPlaying(true);

  useEffect(() => {
    if (!isPlaying) return undefined;

    const pauseIframePlayer = () => {
      const iframeWindow = iframeRef.current && iframeRef.current.contentWindow;
      if (!iframeWindow) return;

      if (provider === 'youtube') {
        iframeWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
      }

      if (provider === 'vimeo') {
        iframeWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    };

    const handlePointerDownOutside = event => {
      const container = containerRef.current;
      if (!container) return;
      if (!container.contains(event.target)) {
        pauseIframePlayer();
      }
    };

    const handleScroll = () => {
      pauseIframePlayer();
    };

    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isPlaying, provider]);

  if (!provider || !id) return null;

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          paddingTop: '56.25%',
          backgroundColor: '#000',
          overflow: 'hidden',
        }}
      >
        {!isPlaying ? (
          <button
            type="button"
            onClick={handlePlay}
            onTouchEnd={handlePlay}
            aria-label="Play video"
            style={{
              all: 'unset',
              position: 'absolute',
              inset: 0,
              cursor: 'pointer',
              display: 'block',
            }}
          >
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt=""
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#111',
                }}
              />
            )}

            <span
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 156,
                height: 156,
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.65)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '25px solid transparent',
                  borderBottom: '25px solid transparent',
                  borderLeft: '38px solid #fff',
                  marginLeft: 4,
                }}
              />
            </span>
          </button>
        ) : (
          <iframe
            ref={iframeRef}
            title="Video player"
            src={embedUrl}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        )}
      </div>
    </div>
  );
}

VideoPlayer.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string,
};

VideoPlayer.defaultProps = {
  thumbnailUrl: '',
};

export default VideoPlayer;
